import {Exclude, Expose, instanceToPlain, plainToClass, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity, EntityIdentifier, EntityObject, isEntity} from "../entity";
import {TimeGroup, toTimeGroup} from "../time-group";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {RegionEntity} from "../region";
import {Quantity} from "../../types/types";
import {FreightTypeEntity} from "../freight-type";
import {Exportable} from "../exportable";
import {adjustFromTimeZone, format} from "../../date/date";
import {Default} from "../../util/decorators";

export const PipelineFlowValidator = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    timeGroup: t.string,
    dttm: t.number,
    quantity: t.number,
    unitOfMeasure: t.string,
    regionId: t.string,
    meterGenerated: t.boolean,
  }),
  t.partial({
    virtual: t.boolean,
    objectName: t.string,
    freightTypeId: t.string,
    freightTypeName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const PipelineFlowArrayValidator = t.array(PipelineFlowValidator);

export type PipelineFlow = t.TypeOf<typeof PipelineFlowValidator>;
export type PipelineFlowArray = t.TypeOf<typeof PipelineFlowArrayValidator>;

@Exclude()
export class PipelineFlowEntity implements Entity, Exportable {
  @Expose()
  id!: string;

  @Expose()
  objectName?: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Transform(getDatabaseCodeTransformer(toTimeGroup))
  @Expose()
  timeGroup!: TimeGroup;

  @Expose()
  dttm!: number;

  @Expose()
  quantity!: number;

  @Expose()
  freightTypeId?: string;

  @Expose()
  freightTypeName?: string;

  @Expose()
  unitOfMeasure!: string;

  @Expose()
  regionId!: string;

  @Expose()
  meterGenerated!: boolean

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  @Default(false)
  virtual = false

  readonly entityType = 'PIPE_FLOW';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for PipelineFlowEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(pipelineFlow: PipelineFlow): PipelineFlowEntity {
    return plainToClass(PipelineFlowEntity, pipelineFlow, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<PipelineFlow> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(PipelineFlowValidator, object);
  }

  asTypeValue(): PipelineFlow {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(PipelineFlowValidator, object);
  }

  setRegion(value: RegionEntity | string) {
    if (typeof value === 'string') {
      this.regionId = value;
    } else {
      this.regionId = value.id;
    }
    return this;
  }

  setObject(value: Entity | EntityObject) {
    if (isEntity(value)) {
      this.objectId = value.id;
      this.objectType = value.entityType;
      this.objectName = value.displayName;
    } else {
      this.objectId = value.objectId;
      this.objectType = value.objectType;
      this.objectName = value.objectName;
    }
    return this;
  }

  setQuantity(quantity: Quantity) {
    this.quantity = quantity.number;
    this.unitOfMeasure = quantity.units;
    return this;
  }

  setFreightType(value: FreightTypeEntity | string | undefined) {
    if (typeof value === 'string') {
      this.freightTypeId = value;
    } else {
      this.freightTypeId = value?.id;
      this.freightTypeName = value?.name;
    }
    return this;
  }

  readonly CSV_HEADERS: string[] = [
    'Date',
    'Time Group',
    'Object',
    'Object Type',
    'Quantity',
    'Unit Of Measure',
    'Freight Type',
    'Meter Generated',
  ];

  asCsv(): string[] {
    return [
      format(adjustFromTimeZone(this.dttm), 'yyyy-MM-dd HH:mm:ss'),
      this.timeGroup.code,
      this.objectName ?? '',
      this.objectType ?? '',
      this.quantity.toString(),
      this.unitOfMeasure,
      this.freightTypeName ?? '',
      this.meterGenerated.toString(),
    ];
  }
}

export const PipelineFlowImportResultsValidator = t.intersection([
  t.type({
    pipelineFlows: t.array(PipelineFlowValidator),
    missingMappings: t.array(t.string),
  }),
  t.partial({
    // partial props here
  }),
]);

export type PipelineFlowImportResults = t.TypeOf<
  typeof PipelineFlowImportResultsValidator
>;

export interface PipelineFlowSearchCriteria {
  fromDttm?: number;
  toDttm?: number;
  regionIds?: string[];
  freightTypeIds?: string[];
  entityIds?: EntityIdentifier[];
  meterGenerated?: boolean
  ignoreMeterGenerated?: boolean
}
